import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Modal, { ModalProps } from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import { Button, Cascader, Col, DatePicker, Divider, Form, Input, Row, Select, Spin } from 'antd';
import {
	bitrixUserGetById,
	bitrixUserListForSelect,
	bitrixUserSearch,
	formatBitrixUserLabel,
} from 'utils/bitrix24/userSearch';
import { CardOutlinedStyled } from 'components/MarketingCampaign/styles';
import Title from 'antd/lib/typography/Title';
import { CascaderOptionType } from 'antd/lib/cascader';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { apiV2 } from 'services/api';
import CRMField from './types/CrmFields.type';
import { DeleteOutlined } from '@ant-design/icons';
import '../../../../styles/SegmentModal.css';
import { LanguageContext } from 'contexts/LanguageContext';

const SelectWithChildren = Select as any;

type BitrixUserPickerOption = { value: string; label: string };

type BitrixUserPickerProps = {
	placeholder: string;
	value: string | undefined;
	options: BitrixUserPickerOption[];
	searchLoading: boolean;
	onValueChange: (value: string) => void;
	onDropdownOpen: () => void;
	onSearchDebounced: (text: string) => void;
};

function BitrixUserPicker(props: Readonly<BitrixUserPickerProps>) {
	const handleDropdownVisibleChange = (open: boolean) => {
		if (open) props.onDropdownOpen();
	};
	const handleSearch = (text: string) => {
		props.onSearchDebounced(text);
	};

	return (
		<SelectWithChildren
			showSearch
			allowClear
			filterOption={false}
			size="large"
			style={{ width: '100%' }}
			placeholder={props.placeholder}
			value={props.value}
			onChange={(value: string | undefined) => props.onValueChange(value ?? '')}
			onDropdownVisibleChange={handleDropdownVisibleChange}
			onSearch={handleSearch}
			notFoundContent={props.searchLoading ? <Spin size="small" /> : null}
		>
			{props.options.map((opt) => (
				<Select.Option key={opt.value} value={opt.value}>
					{opt.label}
				</Select.Option>
			))}
		</SelectWithChildren>
	);
}

interface SegmentModalProps extends ModalProps {
	onSubmit: () => void;
}

export default function SegmentModal(modalProps: Readonly<SegmentModalProps>) {
	const { dictionary } = useContext(LanguageContext);
	const bitrix24 = useContext(Bitrix24Context);

	const [loading, setLoading] = useState(false);
	const [segmentName, setSegmentName] = useState<string>('');
	const [customFields, setCustomFields] = useState<CRMField[]>([]);
	const [segmentNameError, setSegmentNameError] = useState<string>();
	const [customFieldsErrors, setCustomFieldsErrors] = useState<string[]>([]);
	const [domain, setDomain] = useState<string>();

	const filterPrefix = ['>', '<', '=', '!=', '>=', '<=', '%'];

	type UserOption = { value: string; label: string };
	const [userOptionsByIndex, setUserOptionsByIndex] = useState<Record<number, UserOption[]>>({});
	const [userSearchLoading, setUserSearchLoading] = useState<Record<number, boolean>>({});
	const userFetchSeqRef = useRef<Record<number, number>>({});

	const usersToOptions = (users: Awaited<ReturnType<typeof bitrixUserListForSelect>>): UserOption[] =>
		users.map((u) => ({
			value: String(u.ID),
			label: formatBitrixUserLabel(u),
		}));

	const loadBitrixUserOptions = useCallback((rowIndex: number, searchText: string) => {
		const q = searchText.trim();
		const nextSeq = (userFetchSeqRef.current[rowIndex] ?? 0) + 1;
		userFetchSeqRef.current[rowIndex] = nextSeq;

		setUserSearchLoading((prev) => ({ ...prev, [rowIndex]: true }));

		const apply = (users: Awaited<ReturnType<typeof bitrixUserListForSelect>>) => {
			if (userFetchSeqRef.current[rowIndex] !== nextSeq) return;
			setUserSearchLoading((prev) => ({ ...prev, [rowIndex]: false }));
			setUserOptionsByIndex((prev) => ({ ...prev, [rowIndex]: usersToOptions(users) }));
		};

		if (typeof window === 'undefined' || !window.BX24) {
			apply([]);
			return;
		}

		if (!q) {
			bitrixUserListForSelect().then(apply);
			return;
		}

		bitrixUserSearch(q).then(apply);
	}, []);

	const userSearchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

	const debouncedUserSearch = useCallback(
		(rowIndex: number, text: string) => {
			if (userSearchDebounceTimerRef.current !== undefined) {
				clearTimeout(userSearchDebounceTimerRef.current);
			}
			userSearchDebounceTimerRef.current = setTimeout(() => {
				userSearchDebounceTimerRef.current = undefined;
				loadBitrixUserOptions(rowIndex, text);
			}, 350);
		},
		[loadBitrixUserOptions],
	);

	const cancelDebouncedUserSearch = useCallback(() => {
		if (userSearchDebounceTimerRef.current !== undefined) {
			clearTimeout(userSearchDebounceTimerRef.current);
			userSearchDebounceTimerRef.current = undefined;
		}
	}, []);

	useEffect(() => {
		return () => cancelDebouncedUserSearch();
	}, [cancelDebouncedUserSearch]);

	useEffect(() => {
		customFields.forEach((field, index) => {
			if (field.type !== 'user' || !field.value) return;
			const id = String(field.value);
			bitrixUserGetById(id).then((u) => {
				if (!u) return;
				const label = formatBitrixUserLabel(u);
				setUserOptionsByIndex((prev) => {
					const existing = prev[index] || [];
					if (existing.some((o) => o.value === id)) return prev;
					return { ...prev, [index]: [...existing, { value: id, label }] };
				});
			});
		});
	}, [customFields]);
	const [options, setOptions] = useState<CascaderOptionType[]>([
		{
			value: 'deals',
			label: dictionary.deal,
			isLeaf: false,
		},
		{
			value: 'contacts',
			label: dictionary.contact,
			isLeaf: false,
		},
		{
			value: 'leads',
			label: dictionary.lead,
			isLeaf: false,
		},
		{
			value: 'companies',
			label: dictionary.company,
			isLeaf: false,
		},
	]);

	useEffect(() => {
		if (!bitrix24.infos.domain) return;
		setDomain(bitrix24.infos.domain);
		apiV2.interceptors.request.use((config) => {
			return {
				...config,
				headers: {
					...config.headers,
					'Access-Token': bitrix24.auth.access_token,
					'Refresh-Token': bitrix24.auth.refresh_token,
				},
			};
		});
	}, [bitrix24.auth, bitrix24.infos.domain]);

	useEffect(() => {
		if (segmentName) validateSegmentName();
	}, [segmentName]);

	function onLoadData(selectedOptions?: CascaderOptionType[]) {
		if (!selectedOptions || !domain) return;

		const currentOption = selectedOptions[selectedOptions.length - 1];
		currentOption.loading = true;
		apiV2.get(`${currentOption.value}/${domain}/fields`).then((response) => {
			const data: CRMField[] = response.data;
			currentOption.loading = false;
			currentOption.children = data.map((field) => {
				field.entity = currentOption.value;
				return {
					label: field.title,
					value: JSON.stringify(field),
				};
			});
			setOptions([...options]);
		});
	}

	function onAddCustomField(selectedOption: string[]) {
		if (selectedOption.length <= 1) return;
		const parsedOption: CRMField = JSON.parse(selectedOption[1]);
		const hasField = customFields.find(
			(field) => field.entity + field.key === parsedOption.entity + parsedOption.key,
		);
		if (hasField) return;

		const CrmField: CRMField = JSON.parse(selectedOption[1]);
		setCustomFields([...customFields, CrmField]);
	}

	function deleteCustomField(index: number) {
		setCustomFields(customFields.filter((field, i) => i !== index));
	}

	function onChangeCrmField(index: number, value: string, appendToKey?: boolean) {
		setCustomFields(
			customFields.map((field, i) => {
				if (i === index) {
					if (field.value) validateCustomFields(index);
					if (appendToKey) return { ...field, key: getAppendedKey(field, value) };
					return { ...field, value };
				}
				return field;
			}),
		);
	}

	function removeAnyAppendedValue(field: CRMField) {
		return field.key.replace(new RegExp(filterPrefix.join('|'), 'g'), '');
	}

	function getAppendedKey(field: CRMField, value: string) {
		const key = removeAnyAppendedValue(field);
		return `${value}${key}`;
	}

	function showHelpMessageIfError(value: any) {
		if (!value) return dictionary.field_required;
		return '';
	}

	function validateSegmentName() {
		const errorMessage = showHelpMessageIfError(segmentName);
		setSegmentNameError(errorMessage);
		return errorMessage;
	}

	function validateCustomFields(index?: number) {
		if (customFields.length === 0) return [];

		let errors = customFieldsErrors;
		if (index !== undefined) {
			errors[index] = showHelpMessageIfError(customFields[index].value);
		} else {
			errors = customFields.map((field) => showHelpMessageIfError(field.value));
		}

		setCustomFieldsErrors(errors);
		return errors;
	}

	function handleSubmit(e: React.MouseEvent<HTMLElement>) {
		const errors: boolean[] = [];
		validateSegmentName() && errors.push(true);
		validateCustomFields().some((error) => error) && errors.push(true);
		if (errors.some((error) => error)) return;

		const dealFields = formatToBitrix24Fields('deals');
		const contactFields = formatToBitrix24Fields('contacts');
		const leadFields = formatToBitrix24Fields('leads');
		const companyFields = formatToBitrix24Fields('companies');

		const segment = {
			segmentName,
			dealFields,
			contactFields,
			leadFields,
			companyFields,
		};

		setLoading(true);
		createSegment(segment).then(() => {
			setLoading(false);
			modalProps.onSubmit();
		});
	}

	async function createSegment(segment: any) {
		return await apiV2.post(`segment/${domain}/create`, segment);
	}

	function formatToBitrix24Fields(entity: string) {
		const entityFields = customFields.filter((field) => field.entity === entity);
		if (entityFields.length === 0) return {};

		return Object.fromEntries(
			entityFields.map((field) => {
				return [field.key, field.value];
			}),
		);
	}

	return (
		<Modal
			{...{
				...modalProps,
				width: 'auto',
				style: {
					marginTop: 24,
					maxHeight: 462,
					maxWidth: 600,
					minWidth: 0,
				},
				footer: (
					<>
						<Button key="back" onClick={modalProps.onCancel}>
							{dictionary.cancel}
						</Button>
						<Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
							{dictionary.save}
						</Button>
					</>
				),
			}}
		>
			<Row>
				<Title level={4}>
					<Text type="gray-9">{dictionary.configure_segment}</Text>
				</Title>
			</Row>
			<CardOutlinedStyled>
				<Row style={{ textAlign: 'left' }}>
					<Text type="gray-9">{`${dictionary.define_campaign_segment}:`}</Text>
				</Row>
				<Row style={{ margin: '4px 0' }} />
				<Row style={{ width: '100%', display: 'flex' }}>
					<Col style={{ width: '100%' }}>
						<Form.Item
							validateStatus={segmentNameError ? 'error' : undefined}
							help={segmentNameError}
						>
							<Input
								name="segmentName"
								size="large"
								placeholder={dictionary.segment_name}
								value={segmentName}
								onChange={(e) => setSegmentName(e.target.value)}
							/>
						</Form.Item>
					</Col>
				</Row>
				{customFields.length > 0 && <Divider style={{ margin: '12px 0' }} />}
				{customFields.map((field, index) => (
					<Row key={index} style={{ marginRight: 7 }}>
						<Row style={{ margin: '4px 0', textAlign: 'left' }}>
							<Text type="gray-9">{`${field.title} (${field.entity})`}:</Text>
						</Row>
						<Row>
							<Col span={22} style={{ paddingRight: 8 }}>
								<Form.Item
									validateStatus={customFieldsErrors[index] ? 'error' : undefined}
									help={customFieldsErrors[index]}
								>
									{!['enumeration', 'stage_semantic', 'date', 'datetime', 'user'].includes(
										field.type,
									) && (
										<Input
											size="large"
											placeholder={field.title}
											value={field.value}
											onChange={(e) => onChangeCrmField(index, e.target.value)}
										/>
									)}
									{(field.type === 'enumeration' || field.type === 'stage_semantic') && (
										<Select
											{...{
												size: 'large',
												placeholder: field.title,
												value: field.value,
												style: { width: '100%' },
												onChange: (value: string) => onChangeCrmField(index, value),
											}}
										>
											{field.items?.map((item) => (
												<Select.Option {...{ value: item.ID }} key={item.ID}>
													{item.VALUE}
												</Select.Option>
											))}
										</Select>
									)}
									{field.type === 'user' &&
										(typeof window !== 'undefined' && window.BX24 ? (
											<BitrixUserPicker
												placeholder={dictionary.segment_user_search_placeholder}
												value={field.value || undefined}
												options={userOptionsByIndex[index] || []}
												searchLoading={Boolean(userSearchLoading[index])}
												onValueChange={(value: string) => onChangeCrmField(index, value)}
												onDropdownOpen={() => {
													cancelDebouncedUserSearch();
													loadBitrixUserOptions(index, '');
												}}
												onSearchDebounced={(text: string) =>
													debouncedUserSearch(index, text)
												}
											/>
										) : (
											<Input
												size="large"
												placeholder={field.title + ' (ID)'}
												value={field.value}
												onChange={(e) => onChangeCrmField(index, e.target.value)}
											/>
										))}
									{['datetime', 'date'].includes(field.type) && (
										<>
											<Col span={10}>
												<Select
													{...{
														size: 'large',
														placeholder: dictionary.conditionals,
														style: { width: '100%', paddingRight: 8 },
														onChange: (value: string) => onChangeCrmField(index, value, true),
													}}
												>
													<Select.Option {...{ value: '>' }}>
														{dictionary.bigger_than}
													</Select.Option>
													<Select.Option {...{ value: '<' }}>
														{dictionary.smaller_than}
													</Select.Option>
													<Select.Option {...{ value: '=' }}>{dictionary.equal_to}</Select.Option>
													<Select.Option {...{ value: '!=' }}>
														{dictionary.different_from}
													</Select.Option>
													<Select.Option {...{ value: '>=' }}>
														{dictionary.bigger_or_equal_to}
													</Select.Option>
													<Select.Option {...{ value: '<=' }}>
														{dictionary.smaller_or_equal_to}
													</Select.Option>
													<Select.Option {...{ value: '%' }}>{dictionary.contains}</Select.Option>
												</Select>
											</Col>
											<Col span={14}>
												<DatePicker
													size="large"
													showTime={field.type === 'datetime'}
													style={{ width: '100%' }}
													onChange={(date, dateString) => onChangeCrmField(index, dateString)}
												/>
											</Col>
										</>
									)}
								</Form.Item>
							</Col>
							<Col span={2}>
								<Button type="danger" size="large" onClick={() => deleteCustomField(index)}>
									<DeleteOutlined />
								</Button>
							</Col>
						</Row>
					</Row>
				))}
				<Row>
					<Cascader
						{...{
							changeOnSelect: true,
							options,
							loadData: onLoadData,
							popupPlacement: 'topLeft',
							onChange: onAddCustomField,
						}}
					>
						<Button type="primary" size="large" style={{ width: '100%' }}>
							{dictionary.add_field}
						</Button>
					</Cascader>
				</Row>
			</CardOutlinedStyled>
		</Modal>
	);
}
